import React from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import useFirestore from "../../../../config/fireBaseHook";

const ProgramDeliveryDropdown = ({
  value,
  onChange,
  name = "ProgramDeliveryID",
  label = "Program Delivery ID",
  fullWidth = true,
  margin = "normal",
  required = false,
  disabled = false,
  // Optional array of ProgramDeliveryIDs to narrow the options to (e.g. the
  // partners a selected instructor actually delivers). Undefined/null means
  // show every partner, same as before this prop existed.
  allowedIds,
  ...otherProps
}) => {
  const {
    data: programDeliveries,
    loading,
    error,
  } = useFirestore("ProgramDelivery");

  const handleChange = (event) => {
    if (onChange) {
      // Create a synthetic event-like object to match the expected format
      const syntheticEvent = {
        target: {
          name: name,
          value: event.target.value,
        },
      };
      onChange(syntheticEvent);
    }
  };

  if (loading) {
    return (
      <FormControl fullWidth={fullWidth} margin={margin} disabled>
        <InputLabel>{label}</InputLabel>
        <Select value="" label={label} disabled {...otherProps}>
          <MenuItem value="">
            <CircularProgress size={20} />
            <span style={{ marginLeft: 8 }}>Loading...</span>
          </MenuItem>
        </Select>
      </FormControl>
    );
  }

  if (error) {
    return (
      <FormControl fullWidth={fullWidth} margin={margin} disabled>
        <InputLabel error>{label}</InputLabel>
        <Select value="" label={label} error disabled {...otherProps}>
          <MenuItem value="">Error loading program deliveries</MenuItem>
        </Select>
      </FormControl>
    );
  }

  // Deduplicate by ProgramDeliveryID so every unique ID always has a menu item,
  // then sort alphabetically by display name.
  const allowedIdSet = allowedIds
    ? new Set(allowedIds.map((id) => String(id)))
    : null;
  const uniqueMap = new Map();
  if (programDeliveries) {
    programDeliveries.forEach((pd) => {
      const id = pd.ProgramDeliveryID;
      if (
        id != null &&
        !uniqueMap.has(String(id)) &&
        (!allowedIdSet || allowedIdSet.has(String(id)))
      ) {
        uniqueMap.set(String(id), pd);
      }
    });
  }
  const sortedProgramDeliveries = Array.from(uniqueMap.values()).sort(
    (a, b) => {
      const aName = a.SOD_NameForApp || String(a.ProgramDeliveryID) || "";
      const bName = b.SOD_NameForApp || String(b.ProgramDeliveryID) || "";
      return aName.localeCompare(bName);
    },
  );

  // Check value against the deduplicated list so the Select value always matches a rendered MenuItem.
  const currentValueExists =
    value != null &&
    value !== "" &&
    sortedProgramDeliveries.some(
      (pd) => String(pd.ProgramDeliveryID) === String(value),
    );

  return (
    <FormControl
      fullWidth={fullWidth}
      margin={margin}
      required={required}
      disabled={disabled}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        value={currentValueExists ? String(value) : ""}
        label={label}
        onChange={handleChange}
        name={name}
        {...otherProps}
        disabled={disabled}
      >
        <MenuItem value="">
          <em>Select a Tech Prep Partner</em>
        </MenuItem>
        {sortedProgramDeliveries.map((programDelivery) => {
          const programDeliveryId = programDelivery.ProgramDeliveryID;
          const displayName =
            programDelivery.SOD_NameForApp || programDeliveryId;
          const pathwayId = programDelivery.PathwayID;
          return (
            <MenuItem
              key={programDelivery.id}
              value={String(programDeliveryId)}
            >
              {pathwayId ? `${displayName} (${pathwayId})` : displayName}
            </MenuItem>
          );
        })}
      </Select>
      {allowedIdSet && sortedProgramDeliveries.length > 1 && (
        <FormHelperText>
          Showing only partners for the selected instructor
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default ProgramDeliveryDropdown;
